import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { mapActions, mapGetters } from 'vuex';

import { downloadMemberStatisticsReport } from '@/api/reports';
import { GET_MEMBER_REPORT, GET_MEMBER_REPORT_RANGE } from '@/store/reports/constants';
import Order from '@/utils/orderUtil';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  LAST_WRITE_OFF_STATUS_TEXT,
  PER_PAGE_OPTIONS,
  RECEIVE_TEXT,
  RECEIVE_TYPE,
  RECEIVING_STATUS,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import { formatPrice } from '@/utils/helper';

export default {
  name: 'MemberDetails',
  data() {
    return {
      dateType: '',
      endDate: '',
      exportBtnLoading: false,
      loading: false,
      memberID: null,
      memberInfo: {},
      openedDetails: [],
      page: FIRST_PAGE,
      pagination: {},
      perPage: DEFAULT_PER_PAGE,
      records: [],
      startDate: '',
    };
  },
  computed: {
    ...mapGetters('reports', {
      dateRange: GET_MEMBER_REPORT_RANGE,
    }),
    disableExport() {
      return !this.formattedRecords?.length || this.isExporting;
    },
    formattedRecords() {
      return this.records?.map(({
        email, receiveAt, receiveTimes, total, unpaid, ...props
      }, index) => ({
        email,
        index,
        receiveAt: receiveAt ? this.getDate(receiveAt, 'MM/DD HH:mm') : '',
        receiveTime: receiveTimes?.length ? this.getDate(receiveTimes[0]?.startTime) : '',
        total,
        unpaid,
        ...props,
      }));
    },
    infoColumns() {
      return [
        {
          title: '人員編號',
          value: this.memberInfo?.serial,
        },
        {
          title: '帳號',
          value: this.memberInfo?.account,
        },
        {
          title: '姓名',
          value: this.memberInfo?.name,
        },
        {
          title: '部門',
          value: this.memberInfo?.department,
        },
        {
          centered: true,
          title: '可參與訂購數',
          value: this.memberInfo?.joinCount,
        },
        {
          centered: true,
          title: '訂單數',
          value: this.memberInfo?.orderCount,
        },
        {
          centered: true,
          title: '總金額',
          value: this.$options.filters.priceFormat(
            this.$options.filters.commaFormat(this.memberInfo?.orderTotal || 0),
          ),
        },
        {
          centered: true,
          title: '未付款金額',
          value: this.$options.filters.priceFormat(
            this.$options.filters.commaFormat(this.memberInfo?.unpaidTotal || 0),
          ),
        },
        {
          centered: true,
          title: '補助總金額',
          value: this.$options.filters.priceFormat(
            this.$options.filters.commaFormat(this.memberInfo?.subsidyTotal || 0),
          ),
        },
        {
          centered: true,
          title: '總自付額',
          value: this.$options.filters.priceFormat(
            this.$options.filters.commaFormat(this.memberInfo?.outOfSubsidyTotal || 0),
          ),
        },
      ];
    },
    dateTypeText() {
      return this.dateType === 'start' ? '開放訂購日範圍' : '領取起始日範圍';
    },
    dateRange() {
      return `${dayjs(this.startDate).format('YYYY/MM/DD')} - ${dayjs(this.endDate).format('YYYY/MM/DD')}`;
    },
    isExporting() {
      return this.exportBtnLoading;
    },
    params() {
      return {
        date_type: this.dateType,
        start_date: dayjs(this.startDate).startOf('day').format('YYYY-MM-DD HH:mm'),
        end_date: dayjs(this.endDate).endOf('day').format('YYYY-MM-DD HH:mm'),
        page: this.page,
        per_page: this.perPage,
      };
    },
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    receivingStatus() {
      return RECEIVING_STATUS;
    },
    total() {
      return this.pagination?.total || 0;
    },
  },
  async created() {
    const { id } = this.$route.params;
    const { date_type: dateType, start_date: startDate, end_date: endDate } = this.$route.query;

    this.memberID = id;
    this.dateType = /start|receive/.test(dateType) ? dateType : 'start';
    this.startDate = dayjs(startDate).isValid() ? dayjs(startDate) : dayjs();
    this.endDate = dayjs(endDate).isValid() ? dayjs(endDate) : dayjs();

    const baseDay = dayjs('2000');
    if (this.endDate.isBefore(baseDay)) {
      this.endDate = dayjs();
    }
    if (this.startDate.isBefore(baseDay)) {
      this.startDate = dayjs();
    }
    if (this.endDate.isBefore(this.startDate)) {
      this.startDate = this.endDate;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.loadData();
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  watch: {
    page() {
      this.loadData();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setPageOrGetData();
      }
    },
  },
  methods: {
    ...mapActions('reports', {
      getMemberReport: GET_MEMBER_REPORT,
    }),
    formatPrice,
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    getOrderDetails(items = []) {
      return items?.map((item = {}) => {
        const {
          entree: {
            name,
            options = [],
          } = {},
          ...props
        } = item || [];

        return {
          name,
          options,
          ...props,
        };
      }) || [];
    },
    groupBuyingStatus(order) {
      return new Order(order).cardTypeDescription;
    },
    groupBuyingStatusColor(order) {
      const orderClass = new Order(order);
      const textColor = {
        cancelled: 'text-gray-500',
        ongoing: 'text-blue-500',
        ended: 'text-black',
      };
      return textColor[orderClass.cardType] || '';
    },
    getDate(date, format = 'YYYY/MM/DD') {
      return dayjs(date).format(format);
    },
    getPaymentStatus(record) {
      const {
        items = [],
        paid,
        total,
        unpaid,
      } = record || {};

      const isCancelled = new Order(record)?.isCancelled;
      if (isCancelled || !items?.length) {
        return '-';
      }
      if (paid >= total) {
        return '付清';
      }
      if (unpaid > 0 && paid > 0) {
        return '部分付清';
      }
      return '未付款';
    },
    getPaymentStatusColor(record) {
      const { items = [], unpaid } = record || {};

      const isCancelled = new Order(record)?.isCancelled;
      if (unpaid > 0 && !isCancelled && items?.length) {
        return 'text-pink-500';
      }
      return '';
    },
    getReceiveStatus(record) {
      const { items = [] } = record || {};
      const isCancelled = new Order(record)?.isCancelled;
      if (isCancelled || !items?.length) {
        return '-';
      }
      return RECEIVE_TEXT[record.receiveStatus] || '-';
    },
    getReceivingTimeText({
      operation = 0,
      operationTime,
    }) {
      if (LAST_WRITE_OFF_STATUS_TEXT[operation] && operationTime) {
        const formattedTime = dayjs(operationTime).format('MM/DD HH:mm');
        return ` (${formattedTime} ${LAST_WRITE_OFF_STATUS_TEXT[operation]})`;
      }
      return '';
    },
    getReceiveStatusColor(record) {
      const { items = [], receiveStatus } = record || {};
      const isCancelled = new Order(record)?.isCancelled;
      if (receiveStatus === RECEIVING_STATUS.UNRECEIVED && !isCancelled && items?.length) {
        return 'text-pink-500';
      }
      return 'text-black';
    },
    isReceived(order) {
      return order.receiveStatus === RECEIVE_TYPE.FULLY;
    },
    itemRowClass(item) {
      if (this.openedDetails.includes(item.index)) {
        return 'item-table__row item-table__row--opened';
      }
      return 'item-table__row';
    },
    async exportReport() {
      try {
        this.exportBtnLoading = true;
        const res = await downloadMemberStatisticsReport(this.memberID, this.params);
        const { content = '', filename } = res || {};
        const blob = new Blob([content], { type: 'text/csv' });

        saveAs(blob, filename);
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.exportBtnLoading = false;
      }
    },
    async loadData() {
      try {
        this.loading = true;
        const { data: { info, records }, link: { pagination = {} } } = await this.getMemberReport({
          id: this.memberID,
          params: this.params,
        });

        this.pagination = pagination;
        this.memberInfo = info;
        this.records = records;
      } catch (ex) {
        const { message = '' } = ex;

        Toast.open({
          message: `讀取報表資料時發生錯誤，請稍後再試（${message}）`,
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    setPageOrGetData() {
      this.memberInfo = {};
      this.records = [];
      this.pagination = {};
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.loadData();
      }
    },
  },
};
